import { Button, FileInput, Label, TextInput } from 'flowbite-react';
import { get } from 'lodash';
import React, { FormEvent, useEffect, useState } from 'react';
import { Img } from 'react-image';
import { useMutation } from 'react-query';
import { useNavigate } from 'react-router-dom';
import Select, { SingleValue } from 'react-select';
import { Dropdown, mentorJobRoleOptions } from 'shared-utils';
import { getPolicyForS3NonVideo, updateMentorById } from 'src/apis/user';
import { useToast } from 'src/context/ToastProvider';
import { useUserContext } from 'src/context/UserProvider';
import { useGetAllSellableSkillsForMentor } from 'src/hooks/skill.hooks';
import { handleS3Upload } from 'src/utils/imageUpload';
import { UIUser, UpdateUserInput } from 'types';
import { Loader } from 'ui';

interface Props {
	userProfileData: UIUser;
}

const EditMentorDetails = ({ userProfileData }: Props) => {
	const toast = useToast();
	const { user } = useUserContext();
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const navigate = useNavigate();
	const [companyLogo, setCompany] = useState<File>();
	const [userState, setUserState] = useState<UpdateUserInput>({});
	const { data: skillsData, isLoading: isSkillsLoading } = useGetAllSellableSkillsForMentor();

	const updateMentorByIdFn = useMutation(['updateUserById'], updateMentorById, {
		onSuccess() {
			toast({
				title: 'Mentor Profile Updated Successfully.',
				type: 'success',
			});
			navigate('/mentors/interviews');
		},
		onError(error: Error) {
			const errorMessage = get(error, ['response', 'data', 'message'], error.message);
			toast({
				title: errorMessage,
				type: 'error',
				duration: 3000,
			});
		},
	});

	const handleChange = (change: Partial<UpdateUserInput>) => {
		setUserState({
			...userState,
			...change,
		});
	};

	const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
		if (!user?._id) return;
		e.preventDefault();

		let mentorCompanyLogo = '';
		if (companyLogo) {
			const fileSizeMB = companyLogo.size / (1024 * 1024); // Convert bytes to MB
			if (fileSizeMB > 5) {
				toast({
					type: 'error',
					title: 'File size exceeds the maximum limit of 5 MB',
					duration: 10000,
				});
				return;
			}
			setIsLoading(true);
			const policy = await getPolicyForS3NonVideo(companyLogo.name, 'mentorCompanyImages', companyLogo.type, true);
			const uploadedUrl = await handleS3Upload(policy, companyLogo);
			if (uploadedUrl) {
				mentorCompanyLogo = uploadedUrl;
			}
			setIsLoading(false);
		}
		let data: UpdateUserInput = {
			...userState,
		};
		if (mentorCompanyLogo) {
			data = {
				...userState,
				profile: {
					...userState.profile,
					mentorCompanyLogo: mentorCompanyLogo,
				},
			};
		}
		updateMentorByIdFn.mutate({ id: user._id, data: data });
	};

	useEffect(() => {
		if (userProfileData) {
			setUserState({
				profile: {
					...userProfileData.profile,
					skills: userProfileData.profile.skills.map(s => s._id),
					profileSkills: userProfileData.profile.profileSkills.map(s => s._id),
				},
			});
		}
	}, [userProfileData]);

	const allSkills = skillsData?.skills;
	const selectedSkills = allSkills?.filter(s => userState.profile?.skills?.includes(s._id)) || [];

	if (isSkillsLoading)
		return (
			<div className="flex items-center justify-center mt-4">
				<Loader />
			</div>
		);
	return (
		<form className="flex flex-col gap-6" onSubmit={handleSubmit}>
			{/* Mentor Current Company */}
			<div className="mb-[8px]">
				<div className="mb-[5px]">
					<Label
						className="font-inter text-[16px] font-[400] dark:text-white"
						htmlFor="company_name"
						value="Company Name"
					/>
					<span className="text-[red]">*</span>
				</div>
				<TextInput
					style={{
						fontFamily: "'Inter', sans-serif",
						fontSize: '14px',
						maxWidth: '490px',
					}}
					id="company_name"
					name="company_name"
					value={userState.profile?.currentCompany || ''}
					placeholder="LevelUp"
					onChange={e =>
						handleChange({
							profile: {
								...userState.profile,
								currentCompany: e.target.value,
							},
						})
					}
				/>
			</div>

			{/* Job Role */}
			<div>
				<div>
					<Label className="font-inter text-[16px] font-[400] dark:text-white" htmlFor="job_role" value="Job Role" />
					<span className="text-[red]">*</span>
				</div>
				<Select
					options={mentorJobRoleOptions}
					menuPlacement={'auto'}
					classNamePrefix="my-select-plans"
					className="single-value-select-tag !max-w-[490px]"
					instanceId="job-role"
					value={mentorJobRoleOptions.find(jobRole => userState.profile?.currentRole === jobRole.value)}
					onChange={(val: SingleValue<Dropdown>) =>
						handleChange({
							profile: {
								...userState.profile,
								currentRole: val?.value,
							},
						})
					}
				/>
			</div>

			{/* Tech Experience */}
			<div>
				<div>
					<Label
						className="font-inter text-[16px] font-[400] dark:text-white"
						htmlFor="tech_experience"
						value={'Total Tech Experience (in years) '}
					/>
					<span className="text-[red]">*</span>
				</div>
				<TextInput
					style={{
						fontFamily: "'Inter', sans-serif",
						fontSize: '14px',
						maxWidth: '490px',
					}}
					value={userState.profile?.totalExperience}
					onChange={e => handleChange({ profile: { ...userState.profile, totalExperience: Number(e.target.value) } })}
				/>
			</div>

			{/* Linkedin Url */}
			<div>
				<div>
					<Label
						className="font-inter text-[16px] font-[400] dark:text-white"
						htmlFor="linkedin_url"
						value={'LinkedIn URL '}
					/>
				</div>
				<TextInput
					name="linkedin_url"
					type="url"
					style={{
						fontFamily: "'Inter', sans-serif",
						fontSize: '14px',
						maxWidth: '490px',
					}}
					value={userState.profile?.linkedInProfile}
					onChange={e =>
						handleChange({
							profile: {
								...userState.profile,
								linkedInProfile: e.target.value,
							},
						})
					}
				/>
			</div>

			{/* Skills */}
			<div className="mb-2 block">
				<Label className="font-inter text-[16px] font-[400] dark:text-white" htmlFor="skills" value="Skills " />
				<Select
					instanceId="skills"
					menuPlacement={'auto'}
					styles={{
						multiValue: (baseStyles, state) => ({
							...baseStyles,
							backgroundColor: '#111E2C',
						}),
					}}
					classNamePrefix="my-select-plans"
					className="single-value-select-tag !max-w-[490px] hover:cursor-not-allowed"
					closeMenuOnSelect={false}
					isMulti={true}
					value={selectedSkills?.map(skill => ({
						label: skill.name,
						value: skill._id,
					}))}
					isDisabled
				/>
			</div>

			{/* Company Logo */}
			<div>
				<div>
					<Img src={userState.profile?.mentorCompanyLogo || ''} alt="profileImage" height={50} width={50} />
				</div>
				<div className="mb-2 block">
					<Label
						className="font-inter text-[16px] font-[400] dark:text-white"
						htmlFor="iconUrl"
						value="Company Logo "
					/>
				</div>
				<FileInput
					id="file"
					disabled={isLoading}
					style={{
						fontFamily: "'Inter', sans-serif",
						fontSize: '14px',
						maxWidth: '490px',
					}}
					helperText="Upload files upto 2MB only"
					className="text-xs font-normal italic"
					onChange={event => {
						if (event.target.files) {
							setCompany && setCompany(event.target.files[0]);
						}
					}}
				/>
			</div>

			<div className="w-full flex md:justify-end max-w-[490px]">
				<Button
					color="purplePlaced"
					className="w-full md:w-[260px]"
					type="submit"
					disabled={
						!userState.profile?.currentCompany ||
						!userState.profile.currentRole ||
						typeof userState.profile.totalExperience !== 'number' ||
						updateMentorByIdFn.isLoading ||
						isLoading
					}
				>
					{updateMentorByIdFn.isLoading || isLoading ? <Loader /> : 'Update'}
				</Button>
			</div>
		</form>
	);
};

export default EditMentorDetails;
